import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import closeIcon from "../../assets/close-icon.png"
import checkIcon from "../../assets/check-filled-icon.png"
import { useLanguage, getGeneratedSuccessfullyModalTranslation } from '../../hooks/useLanguage';
import { 
  resetDocumentsState, 
  setExtractedData, 
  setEditableData,
  setStepsCompleted, 
  clearValidationErrors, 
  clearTaricJsonErrors, 
  resetSelectedSender, 
  resetSelectedReceiver, 
  resetSelectedAgent,
  resetTabs,
  updateWorkflowSessionState,
  deleteWorkflowSession,
  createWorkflowSession
} from '../../store/reducers';
import { clearAllTabsFromSession } from '../../utils/tabStorage';
import { clearWorkflowSessions } from '../../utils/workflowSessionStorage';

export const DemoGeneratedSuccessfullyModal = ({ 
  setIsSuccessfulModalOpen,
  setIsProcessing,
  setIsReviewConfirmed,
  extractedData,
  activeWorkflowSessionId,
  workflowSessions
}) => {
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  // Get rate limit from Redux to preserve it
  const rateLimit = useSelector((state) => state.ui?.rateLimit);
  
  // Get export numbers from extractedData
  const getExportNumbers = () => {
    if (!extractedData) return ['1'];
    
    const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
    if (declarations.length === 0) return ['1'];
    return declarations.map((_, index) => `${index + 1}`);
  };
  
  const exportNumbers = getExportNumbers();
  const formatExpedientes = (numbers) => {
    if (numbers.length === 0) return '1';
    return numbers.join(', ');
  };

  const handleClose = async () => {
    // 1. Clear URL parameters
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // 2. Clear all workflow sessions from sessionStorage
    clearWorkflowSessions();
    
    // 4. Clear all tabs from sessionStorage
    clearAllTabsFromSession();
    
    // 5. Preserve rate limit in sessionStorage before clearing
    if (rateLimit) {
      sessionStorage.setItem('rateLimit', JSON.stringify(rateLimit));
    }
    
    // 6. Clear essential sessionStorage items (keep only login/auth items and registration data)
    const essentialKeys = [
      'token', 
      'refresh_token', 
      'email', 
      'expires', 
      'user_id', 
      'isDemo',
      'register_email',
      'register_company',
      'register_expectedDuas',
      'register_success',
      'rateLimit' // Preserve rate limit
    ];
    const essentialValues = {};
    essentialKeys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        essentialValues[key] = value;
      }
    });
    
    // Clear all sessionStorage
    sessionStorage.clear();
    
    // Restore essential values
    Object.keys(essentialValues).forEach(key => {
      sessionStorage.setItem(key, essentialValues[key]);
    });
    
    // Set flag to prevent redirect to /register after reload
    sessionStorage.setItem('skipRegisterRedirect', 'true');
    
    // Restore rate limit to Redux after preserving in sessionStorage
    if (rateLimit) {
      dispatch({ type: 'SET_RATE_LIMIT', payload: rateLimit });
    }
    
    // 7. Clear documents and uploaded files from Redux (must be done before resetDocumentsState)
    dispatch({ type: 'CLEAR_DOCUMENTS' });
    dispatch({ type: 'SET_UPLOADED_FILES', payload: [] });
    dispatch({ type: 'RESET_UPLOAD_STATUS' });
    
    // 8. Clear workflow session state if active
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
        documents: [],
        hasDocuments: false,
        uploadComplete: false,
        extractedData: null,
        editableData: null,
        stepsCompleted: false,
        validationErrors: [],
        taricJsonErrors: null,
        isProcessing: false,
        selectedSender: [],
        selectedReceiver: [],
        selectedAgent: [],
        preparedJson: null
      }));
      
      // Delete the current workflow session and create a new empty one
      const sessionOrder = workflowSessions?.sessionOrder || [];
      const isOnlyWorkflow = sessionOrder.length <= 1;
      
      if (isOnlyWorkflow) {
        // Create a new empty workflow before deleting the current one
        const newSessionId = `workflow-${workflowSessions?.nextSessionIndex || 1}`;
        const newWorkflowName = `Workflow ${workflowSessions?.nextSessionIndex || 1}`;
        dispatch(createWorkflowSession(newSessionId, newWorkflowName));
      }
      
      // Delete the workflow that was just used
      dispatch(deleteWorkflowSession(activeWorkflowSessionId));
    }
    
    // 9. Clear preparedJson state
    dispatch({ type: 'SET_PREPARED_JSON', payload: null });
    
    // 10. Reset all Redux state
    dispatch(resetDocumentsState());
    dispatch(setExtractedData(null));
    dispatch(setEditableData(null));
    dispatch(setStepsCompleted(false));
    dispatch(clearValidationErrors());
    dispatch(clearTaricJsonErrors());
    dispatch(resetSelectedSender());
    dispatch(resetSelectedReceiver());
    dispatch(resetSelectedAgent());
    dispatch(resetTabs());
    
    // 11. Clear active job to stop any streams
    window.dispatchEvent(new CustomEvent('clearActiveJobId'));
    sessionStorage.removeItem('activeJobId');
    sessionStorage.removeItem('hasCompletedJob');
    sessionStorage.removeItem('documentReadyReceived');
    sessionStorage.removeItem('ocrInputID');
    
    // 12. Close FieldOverview panel since workflow is reset
    window.dispatchEvent(new CustomEvent('fieldOverviewCollapse', { 
      detail: { collapsed: true } 
    }));
    
    // 13. Close the modal
    setIsSuccessfulModalOpen(false);
    
    // 14. Force page reload to ensure everything is reset (after a short delay to allow state updates)
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-999 text-center">
      <div className="bg-white p-4 rounded-xl shadow-lg max-w-md w-full">
        <div className='flex justify-end'>
          <img className='h-6 w-6 cursor-pointer' src={closeIcon} onClick={handleClose} />
        </div>
        <div className="flex flex-col items-center mb-4">
          {/* Always show success icon in demo */}
          <img className="h-24 w-24 mb-2" src={checkIcon} alt="Success" />

          {/* Display export numbers */}
          <h2 className="text-xs font-light mb-1">
            {isSpanish ? "Exportaciones generadas con éxito" : "Exports successfully generated"}
          </h2>
          <h1 className="text-3xl font-bold mb-2 text-template-primary">
            {isSpanish 
              ? `Exportación ${formatExpedientes(exportNumbers)}` 
              : `Export ${formatExpedientes(exportNumbers)}`}
          </h1>
          
          <h2 className="text-xl font-bold mb-2 mt-4">
            {getGeneratedSuccessfullyModalTranslation('taricDeclarationSuccess', isSpanish)}
          </h2>
          
          <p className='opacity-75 text-sm'>
            {isSpanish 
              ? 'Esta es una demostración. La declaración no se ha enviado realmente a TARIC hasta que dispongas de una cuenta oficial.' 
              : 'This is a demonstration. The declaration has not actually been sent to Taric until you have an official account.'}
          </p>
        </div>
      </div>
    </div>
  );
};

